import { ApiClient } from '@listening/api-contract/api-client-tag';
import type {
  FolderDetail,
  FolderSummary,
  PatchUpdateFolderRequest,
  PostCreateFolderRequest,
} from '@listening/api-contract/legacy-client.gen.ts';
import {
  FolderDetail as FolderDetailSchema,
  FolderSummary as FolderSummarySchema,
} from '@listening/api-contract/legacy-client.gen.ts';
import { SchemaUtils } from '@listening/api-contract/schema-utils';
import { Atom, Reactivity } from 'effect/reactivity';
import { Duration, Effect, Schema } from 'effect';
import { apiMutation, ssrApiQuery, withOptimisticUpdate } from './api-runtime';
import { invalidateAudioLists } from './audio-store';
import type { ReactivityKey } from './reactivity-keys';

// Row shape: FolderSummary (id, legacy_id, num_items, path, thumbnail_urls).
// FolderDetail (adds audio_conversion_ids + child_folder_ids) is per-id-fetch
// via getFolderEffect; the files/$folderId route loader prefetches it.
//
// `FolderSummary`/`FolderDetail` are Schema *classes*, but every cached value
// goes through `SchemaUtils.schemaInstanceToPlain` first, so the cache holds
// PLAIN objects. `Plain<T>` reflects that (homomorphic mapped type, no class
// prototype) — without it, spreading a cached row (`{ ...folder, num_items }`)
// trips `no-misused-spread`, which is a false positive on already-plain data.
type Plain<T> = { [K in keyof T]: T[K] };
export type FolderRow = Plain<FolderSummary>;
export type FolderDetailRow = Plain<FolderDetail>;

// —— Effect programs ——

export const listFoldersEffect = Effect.flatMap(ApiClient, (api) =>
  api.Folder.ListFolders().pipe(
    Effect.map((r): ReadonlyArray<FolderRow> =>
      r.folders.map(SchemaUtils.schemaInstanceToPlain),
    ),
  ),
);

export const getFolderEffect = (folderId: string) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Folder.GetFolder({ params: { folderId } }).pipe(
      Effect.map((r) => SchemaUtils.schemaInstanceToPlain(r.folder)),
    ),
  );

export type CreateFolderPayload = Plain<PostCreateFolderRequest>;

export const createFolderEffect = (payload: CreateFolderPayload) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Folder.CreateFolder({ payload }).pipe(
      Effect.map((r) => SchemaUtils.schemaInstanceToPlain(r.folder)),
    ),
  );

export type UpdateFolderPayload = Plain<PatchUpdateFolderRequest>;

export const updateFolderEffect = (
  folderId: string,
  payload: Partial<UpdateFolderPayload>,
) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Folder.UpdateFolder({
      params: { folderId },
      payload: {
        path: payload.path ?? null,
        audio_conversion_ids: payload.audio_conversion_ids ?? null,
      },
    }).pipe(Effect.map((r) => SchemaUtils.schemaInstanceToPlain(r.folder))),
  );

export const deleteFolderEffect = (folderId: string, recursive: boolean) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Folder.DeleteFolder({
      params: { folderId },
      query: { recursive },
    }),
  );

// —— Atoms ——

/** Reactivity keys for folder reads. Each folder's own read refetches on
 * `list` too, so any folder change reaches it. */
export const folderKeys = {
  list: 'folders:list',
} as const satisfies Record<string, ReactivityKey>;

// Folders change only through this client's own writes, which invalidate
// them, so five minutes of freshness avoids refetching on every mount.
const folderStaleTime = Duration.minutes(5);

/** Every folder. A page the server renders sends them along. */
export const foldersAtom = ssrApiQuery(listFoldersEffect, {
  key: 'folders',
  success: Schema.Array(FolderSummarySchema),
  staleTime: folderStaleTime,
  reactivityKeys: [folderKeys.list],
});

/** One folder with its audio and child folder ids. A page the server renders
 * sends it along. */
export const folderDetailAtom = Atom.family((folderId: string) =>
  ssrApiQuery(getFolderEffect(folderId), {
    key: `folder:${folderId}`,
    success: FolderDetailSchema,
    staleTime: folderStaleTime,
    reactivityKeys: [folderKeys.list],
  }),
);

/** The server assigns the folder's id, so nothing shows until it answers.
 * Audios filed into it refetch the lists. */
export const createFolderAtom = apiMutation((payload: CreateFolderPayload) =>
  createFolderEffect(payload).pipe(
    Effect.tap(() =>
      payload.audio_conversion_ids?.length ? invalidateAudioLists : Effect.void,
    ),
    Effect.tap(() => Reactivity.invalidate([folderKeys.list])),
  ),
);

export type RenameFolderInput = {
  readonly id: string;
  readonly path: ReadonlyArray<string>;
};

const withPath =
  (input: RenameFolderInput) =>
  <Row extends { readonly id: string; readonly path: ReadonlyArray<string> }>(
    row: Row,
  ): Row =>
    row.id === input.id ? { ...row, path: [...input.path] } : row;

/**
 * Renames a folder in the folder list and the folder's own read at once, so
 * the files index and the folder page agree. Either may be cold (a folder
 * page opened directly has no list), and the rename is sent regardless. A
 * failed rename puts both back.
 */
export const renameFolderAtom = apiMutation((input: RenameFolderInput) =>
  withOptimisticUpdate(
    foldersAtom,
    (rows) => rows.map(withPath(input)),
    withOptimisticUpdate(
      folderDetailAtom(input.id),
      withPath(input),
      updateFolderEffect(input.id, { path: [...input.path] }),
    ),
  ).pipe(Effect.ensuring(Reactivity.invalidate([folderKeys.list]))),
);

/** Deletes a folder. `recursive` deletes its audios too; otherwise they move
 * home. Either way every list refetches. */
export const deleteFolderAtom = apiMutation(
  (input: { readonly folderId: string; readonly recursive: boolean }) =>
    withOptimisticUpdate(
      foldersAtom,
      (rows) => rows.filter((row) => row.id !== input.folderId),
      deleteFolderEffect(input.folderId, input.recursive),
    ).pipe(Effect.tap(() => invalidateAudioLists)),
  { reactivityKeys: [folderKeys.list] },
);
